import { Loader2, Search } from "lucide-react";

import { useState } from "react";

export function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

const AV = [
  ["#EEF3FB", "#1A3A6D"], ["#ecfdf5", "#059669"], ["#f5f3ff", "#7c3aed"],
  ["#fff1f2", "#e11d48"], ["#FFF9E6", "#9A7008"], ["#E9EFF9", "#0F2A55"],
];

export function Avatar({ name = "", size = 38, seed = 0, src = null }) {
  const [broken, setBroken] = useState(false);
  const c = AV[(name.charCodeAt(0) + seed) % AV.length] || AV[0];
  if (src && !broken) {
    return (
      <div style={{ width: size, height: size }} className="rounded-full overflow-hidden shrink-0 bg-slate-100" data-testid="avatar-photo">
        <img src={src} alt={name} className="w-full h-full object-cover" onError={() => setBroken(true)} />
      </div>
    );
  }
  return (
    <div style={{ width: size, height: size, background: c[0], color: c[1], fontSize: size * 0.36 }}
      className="rounded-full flex items-center justify-center font-bold shrink-0 font-display">
      {initials(name)}
    </div>
  );
}

export function Card({ className = "", children, hover = false, ...rest }) {
  return <div className={`pq-card ${hover ? "pq-card-hover" : ""} ${className}`} {...rest}>{children}</div>;
}

export function CardHead({ title, action, icon }) {
  return (
    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
      <h3 className="font-display font-semibold text-[15px] flex items-center gap-2 text-slate-800">
        {icon}{title}
      </h3>
      {action}
    </div>
  );
}

export function Btn({ variant = "", size = "", className = "", children, ...rest }) {
  const v = variant === "primary" ? "pq-btn-primary" : variant === "danger" ? "pq-btn-danger" : "";
  const s = size === "sm" ? "pq-btn-sm" : "";
  return <button className={`pq-btn ${v} ${s} ${className}`} {...rest}>{children}</button>;
}

export function Badge({ tone = "navy", children, className = "" }) {
  const tones = {
    teal: "bg-navy-50 text-navy-700",
    navy: "bg-navy-50 text-navy-700",
    gold: "bg-gold-50 text-gold-600 ring-1 ring-gold-400/30",
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    indigo: "bg-indigo-50 text-indigo-700",
    purple: "bg-violet-50 text-violet-700",
    slate: "bg-slate-100 text-slate-600",
  };
  return <span className={`pq-badge ${tones[tone] || tones.slate} ${className}`}>{children}</span>;
}

export function SearchInput({ value, onChange, placeholder = "Search…", className = "sm:w-64", testid }) {
  return (
    <div className={`relative w-full ${className}`}>
      <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="pq-input pl-9 py-2 text-[13.5px]" data-testid={testid} />
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <Loader2 className={`animate-spin ${className}`} />;
}

export function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4">{icon}</div>
      <h3 className="font-display font-semibold text-slate-800 text-lg">{title}</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-sm">{subtitle}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function statusTone(status) {
  return {
    "On Track": "emerald", "At Risk": "rose", "Review Needed": "amber",
    "New": "sky", "In Progress": "teal", "In treatment": "teal", "Review": "purple", "Completed": "slate",
  }[status] || "slate";
}
